/* tslint:disable */

"use client";

import { useParams, useNavigate } from "react-router-dom";
import {
  Flex,
  Heading,
  Text,
  Link,
  Grid,
  Collection,
  Card,
  Tabs,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
} from "@aws-amplify/ui-react";
import { FileUploader } from "@aws-amplify/ui-react-storage";
import React from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";

import { generateClient } from "aws-amplify/api";

import { Helmet } from "react-helmet";
import { Schema } from "../../../amplify/data/resource";
import { hasAccess } from "../../acl/groups";
import RoutinePatternForm from "../../ui-components/RoutinePatternForm";
import {
  RoutinePattern,
  RoutinePatternContent,
} from "../../models/RoutinePattern";
import { UserVideo } from "../../models/UserVideo";
import VideoCarousel from "../../components/VideoCarousel";
import {
  FaArrowLeft,
  FaMagnifyingGlass,
  FaPencil,
  FaPlus,
  FaRegTrashCan,
  FaTable,
  FaTrello,
} from "react-icons/fa6";
import { I18n } from "aws-amplify/utils";
import LoadingDialog from "../../components/LoadingDialog";
import { fetchAuthSession } from "aws-amplify/auth";
import { DialogContent } from "@mui/material";
import PaperComponent from "../../components/PaperComponent";
import { calculateBeats } from "../../utils/rhythm";

const client = generateClient<Schema>();

type Pattern = Schema["Pattern"]["type"];
type Variation = Schema["Variation"]["type"];
type DanceStyle = Schema["DanceStyle"]["type"];
type Routine = Schema["Routine"]["type"];

interface RoutineViewProps {
  groups: string[];
}

const patternPageSize = 10;

const RoutineView: React.FC<RoutineViewProps> = ({ groups }) => {
  const navigate = useNavigate();

  const { id } = useParams();

  const [routine, setRoutine] = React.useState<Routine>({} as Routine);
  const [danceStyle, setDanceStyle] = React.useState<DanceStyle>(
    {} as DanceStyle
  );
  const [routinePatterns, setRoutinePatterns] = React.useState<
    RoutinePattern[]
  >([]);
  const [routinePatternsContent, setRoutinePatternsContent] = React.useState<
    RoutinePatternContent[]
  >([]);
  const [variationsMap, setVariationsMap] = React.useState<
    Map<string, Variation[]>
  >(new Map<string, Variation[]>());

  const [routinePatternsTableRows, setRoutinePatternsTableRows] =
    React.useState<React.ReactElement[]>([]);

  const [patterns, setPatterns] = React.useState<Pattern[]>([]);
  const [nextOrder, setNextOrder] = React.useState<number>(10);

  const [open, setOpen] = React.useState(false);
  const [videoUploadOpen, setVideoUploadOpen] = React.useState(false);
  const [videos, setVideos] = React.useState<UserVideo[]>();
  const [identityId, setIdentityId] = React.useState<string>("");
  const [tab, setTab] = React.useState("grid");

  const [loadingMap, setLoadingMap] = React.useState<{
    [key: string]: boolean;
  }>({});

  const markLoading = (key: string, value: boolean) => {
    setLoadingMap((prev) => ({ ...prev, [key]: value }));
  };

  const onClickAddPattern = () => {
    setOpen(true);
  };

  const onCloseDialog = () => {
    setOpen(false);
  };

  const onClickAddVideo = () => {
    setVideoUploadOpen(true);
  };

  const onCloseVideoUpload = () => {
    setVideoUploadOpen(false);
  };

  const convertJsonToRoutinePattern = (json: string): RoutinePattern => {
    if (!json) {
      throw new Error("JSON is null");
    }

    const parsed = JSON.parse(json);

    return {
      patternId: parsed.patternId,
      variationId: parsed.variationId,
      order: parsed.order,
      rhythm: parsed.rhythm,
      hold: parsed.hold,
    };
  };

  const convertJsonToUserVideo = (json: string): UserVideo => {
    if (!json) {
      throw new Error("JSON is null");
    }

    const parsed = JSON.parse(json);

    return {
      key: parsed.key,
      identityId: parsed.identityId,
    };
  };

  const updateRoutinePatterns = (newRoutinePatterns: RoutinePattern[]) => {
    markLoading("routine", true);
    client.models.Routine.update({
      id: routine.id,
      patternList: newRoutinePatterns.map((pattern) => JSON.stringify(pattern)),
    });
    markLoading("routine", false);
  };

  React.useEffect(() => {
    fetchAuthSession().then((session) => {
      setIdentityId(session.identityId ?? "");
    });
  }, []);

  React.useEffect(() => {
    markLoading("routine", true);
    const routineSub = client.models.Routine.observeQuery({
      filter: {
        id: { eq: id },
      },
    }).subscribe({
      next: ({ items }) => {
        if (!items || items.length === 0) {
          return;
        }
        // convert patternList to RoutinePattern
        if (items[0].patternList && items[0].patternList.length > 0) {
          const routinePatterns = items[0].patternList.map((pattern) =>
            convertJsonToRoutinePattern(pattern as string)
          );
          routinePatterns.sort((a, b) => a.order - b.order);
          // add index to each routine pattern for deleting later (don't use for looping)
          routinePatterns.forEach((pattern, index) => {
            pattern.index = index;
          });
          // set next order to the last order + 10
          setNextOrder(routinePatterns[routinePatterns.length - 1].order + 10);
          setRoutinePatterns(routinePatterns);
        }
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        setRoutine(items[0]);
        if (items[0].videos && items[0].videos.length > 0) {
          const videos = items[0].videos.map((video) =>
            convertJsonToUserVideo(video as string)
          );
          setVideos(videos);
        }
        markLoading("routine", false);
      },
    });
    return () => {
      routineSub.unsubscribe();
    };
  }, [id]);

  React.useEffect(() => {
    if (routine.danceStyleRoutinesId) {
      markLoading("danceStyle", true);
      const danceStyleSub = client.models.DanceStyle.observeQuery({
        filter: {
          id: { eq: routine.danceStyleRoutinesId },
        },
      }).subscribe({
        next: ({ items }) => {
          setDanceStyle(items[0]);
          markLoading("danceStyle", false);
        },
      });
      markLoading("patterns", true);
      const patternsSub = client.models.Pattern.observeQuery({
        filter: {
          danceStylePatternsId: { eq: routine.danceStyleRoutinesId },
        },
      }).subscribe({
        next: ({ items }) => {
          items.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
          setPatterns([...items]);
          markLoading("patterns", false);
        },
      });
      return () => {
        danceStyleSub.unsubscribe();
        patternsSub.unsubscribe();
      };
    }
  }, [routine.danceStyleRoutinesId]);

  React.useEffect(() => {
    if (patterns.length === 0) return;
    const fetchVariationsPage = async (
      _variationsMap: Map<string, Variation[]> = new Map<string, Variation[]>(),
      prevToken: null | string = null,
      patternsPage: number = 0
    ): Promise<undefined | Map<string, Variation[]>> => {
      // select 10 patterns at a time
      const _patterns = patterns.slice(
        patternsPage,
        patternsPage + patternPageSize
      );
      const {
        data: items,
        errors,
        nextToken,
      } = await client.models.Variation.list({
        filter: {
          or: _patterns.map((pattern) => ({
            patternVariationsId: { eq: pattern.id },
          })),
        },
        nextToken: prevToken,
      });
      if (errors) {
        console.error(errors);
        return;
      }
      items.forEach((variation) => {
        const variations = _variationsMap.get(
          variation.patternVariationsId ?? ""
        );
        if (variations) {
          variations.push(variation);
        } else {
          _variationsMap.set(variation.patternVariationsId ?? "", [variation]);
        }
      });
      if (nextToken) {
        return await fetchVariationsPage(
          _variationsMap,
          nextToken,
          patternsPage
        );
      }
      if (patternPageSize === _patterns.length) {
        return await fetchVariationsPage(
          _variationsMap,
          null,
          patternsPage + patternPageSize
        );
      }
      return _variationsMap;
    };

    const fetchVariations = async () => {
      markLoading("variations", true);
      const variations = await fetchVariationsPage();
      markLoading("variations", false);

      if (!variations) return;

      setVariationsMap(variations);
    };

    fetchVariations();
  }, [patterns]);

  React.useEffect(() => {
    setRoutinePatternsContent(
      routinePatterns.map((pattern, index) => {
        return {
          patternId: pattern.patternId,
          variationId: pattern.variationId,
          order: pattern.order,
          rhythm: pattern.rhythm,
          hold: pattern.hold,
          patternContent: (
            <Link href={`/patterns/${pattern?.patternId}/view`}>
              {patterns.find((p) => p.id === pattern?.patternId)?.name}
            </Link>
          ),
          rhythmContent: pattern?.rhythm.map((value, rindex) => (
            <>
              <Text
                color={value.use ? "font.active" : "font.disabled"}
                textDecoration={value.use ? "" : "line-through"}
                key={`rhythm-${index}-${rindex}`}
              >
                {value.rhythm}
              </Text>
              {variationsMap
                .get(pattern?.patternId)
                ?.find((v) => v.id === pattern?.variationId)?.rhytmMan && (
                <Text>
                  {I18n.get("Man")}:{" "}
                  {
                    variationsMap
                      .get(pattern?.patternId)
                      ?.find((v) => v.id === pattern?.variationId)?.rhytmMan
                  }
                </Text>
              )}
            </>
          )),
          holdContent: pattern?.hold.map((value, hindex) => (
            <Text
              color={value.use ? "font.active" : "font.disabled"}
              textDecoration={value.use ? "" : "line-through"}
              key={`rhythm-${index}-${hindex}`}
            >
              {value.hold}
            </Text>
          )),
          index: pattern.index,
        };
      })
    );
  }, [routinePatterns, variationsMap, patterns]);

  React.useEffect(() => {
    // use a while loop and increment index when number of beats are sufficient
    const beatsPerBar = danceStyle.beatsPerBar ?? 4;
    const routinePatternsTableRows = [];
    let index = 0;
    let beatsToGo = 0;
    let beat = 1;
    let beatpair = 1;
    let bar = 1;
    let barpairbar = 1;
    while (index < routinePatternsContent.length || beatsToGo > 0) {
      if (beatsToGo === 0) {
        const pattern = routinePatternsContent[index];
        routinePatternsTableRows.push(
          <TableRow key={`${bar}-${beat}-${index}`}>
            <TableCell>{barpairbar}</TableCell>
            <TableCell>{beatpair}</TableCell>
            <TableCell>{bar}</TableCell>
            <TableCell>{beat}</TableCell>
            <TableCell>{Math.floor(pattern.order / 10)}</TableCell>
            <TableCell>{pattern.patternContent}</TableCell>
            <TableCell>{pattern.rhythmContent}</TableCell>
            <TableCell>{pattern.holdContent}</TableCell>
          </TableRow>
        );
        index++;
        beatsToGo = calculateBeats(
          beatsPerBar,
          // only if use is true
          pattern.rhythm.map((r) => (r.use ? r.rhythm : "")).join("")
        );
      } else {
        routinePatternsTableRows.push(
          <TableRow key={`${bar}-${beat}-${index}`}>
            <TableCell>{barpairbar}</TableCell>
            <TableCell>{beatpair}</TableCell>
            <TableCell>{bar}</TableCell>
            <TableCell>{beat}</TableCell>
            <TableCell key={`${bar}-${beat}-${index}-order`}></TableCell>
            <TableCell key={`${bar}-${beat}-${index}-pattern`}></TableCell>
            <TableCell key={`${bar}-${beat}-${index}-rhythm`}></TableCell>
            <TableCell key={`${bar}-${beat}-${index}-hold`}></TableCell>
          </TableRow>
        );
      }
      beatsToGo--;
      beat++;
      beatpair++;
      if (beat > beatsPerBar) {
        bar++;
        beat = 1;
      }
      if (beatpair > beatsPerBar * 2) {
        barpairbar++;
        beatpair = 1;
      }
    }
    setRoutinePatternsTableRows(routinePatternsTableRows);
  }, [routinePatternsContent, danceStyle]);

  const processFile = async ({ file }: { file: File }) => {
    const fileExtension = file.name.split(".").pop();
    const uuidHash = crypto.randomUUID();

    return { file, key: `${uuidHash}.${fileExtension}` };
  };

  return (
    <React.Fragment>
      {Object.values(loadingMap).some((loading) => loading) ? (
        <LoadingDialog loadingMap={loadingMap} />
      ) : (
        <Flex gap="1rem" direction="column">
          <Helmet>
            <title>{routine.name}</title>
          </Helmet>
          <Flex marginTop={"1rem"}>
            <Heading level={1}>{routine.name}</Heading>
          </Flex>
          <Flex>
            <Link onClick={() => navigate(-1)}>
              <FaArrowLeft aria-label={I18n.get("Back")} />
            </Link>
            <Link href={`/routines/${routine.id}/update`}>
              <FaPencil aria-label={I18n.get("Edit")} />
            </Link>
          </Flex>
          <Flex direction="column">
            <Flex>
              <Heading>{I18n.get("Dance Style")}</Heading>
              <Link href={`/dance-styles/${routine.danceStyleRoutinesId}/view`}>
                {danceStyle ? danceStyle.name : routine.danceStyleRoutinesId}
              </Link>
            </Flex>
            <Flex>
              <Heading>{I18n.get("Status")}</Heading>
              <Text>
                {routine.status ? I18n.get("Active") : I18n.get("Disabled")}
              </Text>
            </Flex>
          </Flex>
          <Flex>
            <Heading level={2}>{I18n.get("Videos")}</Heading>
          </Flex>

          <Flex>
            <VideoCarousel
              videos={videos ?? []}
              onVideoUploadClick={onClickAddVideo}
              groups={groups}
            />
          </Flex>
          <Flex>
            <Heading level={2}>{I18n.get("Patterns")}</Heading>
          </Flex>
          <Flex>
            <Link onClick={onClickAddPattern}>
              <FaPlus aria-label={I18n.get("Add Pattern to Routine")} />
            </Link>
          </Flex>
          <Tabs
            value={tab}
            onValueChange={(tab) => setTab(tab)}
            items={[
              {
                value: "grid",
                label: <FaTrello aria-label={I18n.get("Grid")} />,
                content: (
                  <Grid>
                    <Collection
                      items={routinePatternsContent}
                      type="list"
                      direction="row"
                      wrap="wrap"
                      alignContent="flex-start"
                      searchPlaceholder={I18n.get("Type to search...")}
                      isPaginated
                      isSearchable
                    >
                      {(pattern: RoutinePatternContent, index: number) => (
                        <Card
                          key={index}
                          title={
                            I18n.get("Pattern #") + (pattern?.order ?? 0) / 10
                          }
                          variation="outlined"
                          borderRadius="medium"
                          alignSelf="flex-start"
                          width={{
                            base: "100%",
                            medium: "48.8%",
                            large: "18.9%",
                          }}
                        >
                          <Flex
                            direction="column"
                            marginBottom="1rem"
                            minHeight={{ base: "2lh", large: "3lh" }}
                            textAlign="center"
                          >
                            <Heading>{pattern.patternContent}</Heading>
                          </Flex>
                          <Flex
                            direction="row"
                            wrap="wrap"
                            justifyContent="center"
                            marginBottom="1rem"
                          >
                            {pattern.rhythmContent}
                          </Flex>

                          <Flex
                            direction="row"
                            wrap="wrap"
                            justifyContent="center"
                            marginBottom="1rem"
                          >
                            {pattern.holdContent}
                          </Flex>

                          <Flex justifyContent="center" marginBottom="1rem">
                            <Link
                              href={`/variations/${pattern?.variationId}/view`}
                            >
                              <FaMagnifyingGlass
                                aria-label={I18n.get("View Variation")}
                              />
                            </Link>
                            {hasAccess(groups, "routines", "update") && (
                              <Link
                                onClick={() => {
                                  // remove pattern at index from routinePatterns
                                  const newRoutinePatterns =
                                    routinePatterns.filter(
                                      (_) => _.index !== pattern.index
                                    );
                                  updateRoutinePatterns(newRoutinePatterns);
                                }}
                              >
                                <FaRegTrashCan
                                  aria-label={I18n.get("Delete")}
                                />
                              </Link>
                            )}
                          </Flex>
                        </Card>
                      )}
                    </Collection>
                  </Grid>
                ),
              },
              {
                value: "table",
                label: <FaTable aria-label={I18n.get("Table")} />,
                content: (
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell as="th">{I18n.get("Bar Pair #")}</TableCell>
                        <TableCell as="th">{I18n.get("Beat Pair #")}</TableCell>
                        <TableCell as="th">{I18n.get("Bar #")}</TableCell>
                        <TableCell as="th">{I18n.get("Beat #")}</TableCell>
                        <TableCell as="th">{I18n.get("Order")}</TableCell>
                        <TableCell as="th">{I18n.get("Pattern")}</TableCell>
                        <TableCell as="th">{I18n.get("Rhythm")}</TableCell>
                        <TableCell as="th">{I18n.get("Hold")}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>{routinePatternsTableRows}</TableBody>
                  </Table>
                ),
              },
            ]}
          />

          <Dialog
            open={open}
            onClose={onCloseDialog}
            PaperComponent={PaperComponent}
            aria-labelledby="draggable-dialog-title"
          >
            <DialogTitle id="draggable-dialog-title" style={{ cursor: "move" }}>
              {I18n.get("Add Pattern to Routine")}
            </DialogTitle>
            <DialogContent>
              <RoutinePatternForm
                patterns={patterns}
                variations={variationsMap}
                nextOrder={nextOrder}
                saveRoutinePattern={(pattern) => {
                  const newRoutinePatterns = [...routinePatterns, pattern];
                  newRoutinePatterns.sort((a, b) => a.order - b.order);
                  updateRoutinePatterns(newRoutinePatterns);
                  onCloseDialog();
                }}
              />
            </DialogContent>
          </Dialog>

          <Dialog open={videoUploadOpen} onClose={onCloseVideoUpload}>
            <DialogTitle>{I18n.get("Upload Videos")}</DialogTitle>
            <DialogContent>
              {identityId ? (
                <FileUploader
                  acceptedFileTypes={["video/*"]}
                  maxFileCount={5}
                  maxFileSize={50000000}
                  processFile={processFile}
                  isResumable
                  onUploadSuccess={(file) => {
                    console.log("Upload success:", file);
                    if (!file.key) return;

                    const videos = routine.videos ?? [];
                    videos.push({ key: file.key, identityId });
                    client.models.Routine.update({
                      id: routine.id,
                      videos: videos.map((video) => JSON.stringify(video)),
                    });
                    onCloseVideoUpload();
                  }}
                  onUploadError={(error, { key }) => {
                    console.error(
                      "Upload error for key:",
                      key,
                      "Error:",
                      error
                    );
                  }}
                  onUploadStart={({ key }) => {
                    console.log("Upload started for key:", key);
                    console.log("Current identityId:", identityId);
                  }}
                  path={({ identityId }) =>
                    `protected/${identityId}/routine/${routine.id}/`
                  }
                />
              ) : (
                <div>Loading authentication...</div>
              )}
            </DialogContent>
          </Dialog>
        </Flex>
      )}
    </React.Fragment>
  );
};

export default RoutineView;
